import React from 'react';

interface TooltipPayloadItem {
  name?: string;
  value?: number | string | null;
  fill?: string;
}

interface SentimentTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string | number;
}

interface SafetyTooltipDatum {
  fullTitle?: string;
  views?: string | number;
  comments?: number;
}

interface SafetyTooltipPayloadItem extends TooltipPayloadItem {
  payload?: SafetyTooltipDatum;
}

interface SafetyTooltipProps {
  active?: boolean;
  payload?: SafetyTooltipPayloadItem[];
}

/* ─── SentimentTooltip ───────────────────────────────────────────────────── */

export function SentimentTooltip({ active, payload, label }: SentimentTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: 10,
        padding: '10px 14px',
        
        fontSize: 12,
      }}
    >
      <div className="font-semibold text-foreground" style={{ marginBottom: 7 }}>{label}</div>
      {payload.map((p) => (
        <div
          key={p.name}
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}
        >
          <span
            style={{ width: 8, height: 8, borderRadius: 2, background: p.fill, flexShrink: 0 }}
          />
          <span className="text-muted-foreground">{p.name}:</span>
          <span style={{ fontWeight: 700, color: p.fill }}>{Number(p.value).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

/* ─── SafetyTooltip ──────────────────────────────────────────────────────── */

export function SafetyTooltip({ active, payload }: SafetyTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E5E5E5',
        borderRadius: 12,
        padding: '12px 16px',
        
        minWidth: 200,
      }}
    >
      <div
        className="text-body-sm-semibold text-foreground"
        style={{
          marginBottom: 8,
          lineHeight: 1.4,
        }}
      >
        {d?.fullTitle}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {payload.map((p) => (
          <div
            key={p.name}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 16,
            }}
          >
            <span className="flex items-center text-caption-medium text-muted-foreground" style={{ gap: 5 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: p.fill,
                  flexShrink: 0,
                  display: 'inline-block',
                }}
              />
              {p.name}
            </span>
            <span style={{ fontSize: 14, fontWeight: 800, color: p.fill }}>
              {p.value == null ? 'N/A' : `${p.value} / 100`}
            </span>
          </div>
        ))}
      </div>
      {d?.views && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px solid #f5f5f5',
            fontSize: 10.5,
            color: '#737373',
          }}
        >
          👁 {d?.views} views · 💬 {d?.comments?.toLocaleString()} comments analyzed
        </div>
      )}
    </div>
  );
}

/* ─── SentimentSegmentBar ────────────────────────────────────────────────── */

export function SentimentSegmentBar({ pos, neu, neg }: { pos: number; neu: number; neg: number }) {
  const total = pos + neu + neg || 1;
  const pPct = Math.round((pos / total) * 100);
  const nePct = Math.round((neu / total) * 100);
  const ngPct = 100 - pPct - nePct;
  return (
    <div>
      <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', gap: 2 }}>
        {pPct > 0 && (
          <div
            style={{
              width: `${pPct}%`,
              background: 'linear-gradient(90deg,#059669,#22c55e)',
              borderRadius: '6px 0 0 6px',
            }}
          />
        )}
        {nePct > 0 && <div style={{ width: `${nePct}%`, background: '#E5E5E5' }} />}
        {ngPct > 0 && (
          <div
            style={{
              width: `${ngPct}%`,
              background: 'linear-gradient(90deg,#f97316,#dc2626)',
              borderRadius: '0 6px 6px 0',
            }}
          />
        )}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
        <span
          style={{
            fontSize: 12,
            color: '#059669',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: '#059669',
              display: 'inline-block',
            }}
          />
          Positive {pPct}%
        </span>
        <span
          style={{
            fontSize: 12,
            color: '#737373',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: '#E5E5E5',
              display: 'inline-block',
            }}
          />
          Neutral {nePct}%
        </span>
        <span
          style={{
            fontSize: 12,
            color: '#dc2626',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: '#dc2626',
              display: 'inline-block',
            }}
          />
          Negative {ngPct}%
        </span>
      </div>
    </div>
  );
}
